import { useState, useEffect, useRef } from 'react';
import { isPointerOverPortaledOverlayFromEvent } from './utils/overlayPointerGuard';

/**
 * Hook for tracking which node is currently hovered
 * Used by HandlesLayer to show connection handles on hover
 *
 * Uses event delegation for performance (single listener on container)
 */
export function useHoveredNode(containerRef) {
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const currentHoveredRef = useRef(null);
  const clearHoverTimeoutRef = useRef(null);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return undefined;

    const setHovered = (taskId) => {
      if (clearHoverTimeoutRef.current) {
        clearTimeout(clearHoverTimeoutRef.current);
        clearHoverTimeoutRef.current = null;
      }
      if (!taskId) {
        if (currentHoveredRef.current) {
          currentHoveredRef.current = null;
          setHoveredNodeId(null);
        }
        return;
      }
      if (taskId !== currentHoveredRef.current) {
        currentHoveredRef.current = taskId;
        setHoveredNodeId(taskId);
      }
    };

    const scheduleClearHover = () => {
      if (clearHoverTimeoutRef.current) return;
      clearHoverTimeoutRef.current = setTimeout(() => {
        clearHoverTimeoutRef.current = null;
        currentHoveredRef.current = null;
        setHoveredNodeId(null);
      }, 120);
    };

    const handleMouseMove = (e) => {
      if (isPointerOverPortaledOverlayFromEvent(e)) {
        if (currentHoveredRef.current) scheduleClearHover();
        return;
      }

      const target = e.target;

      const node = target.closest?.('.task-node, .decision-node, .milestone-node');
      if (node) {
        const taskId = node.dataset?.taskId;
        if (taskId) setHovered(taskId);
        return;
      }

      const handleElement = target.closest?.(
        '.handles-layer__drag-handle, '
        + '.handles-layer__connection-handle, '
        + '.handles-layer__decision-group, '
        + '.handles-layer__branch-btn'
      );
      if (handleElement) {
        const handleTaskId = handleElement.dataset?.taskId;
        if (handleTaskId) setHovered(handleTaskId);
        return;
      }

      if (currentHoveredRef.current) scheduleClearHover();
    };

    const handleMouseLeave = () => {
      if (clearHoverTimeoutRef.current) {
        clearTimeout(clearHoverTimeoutRef.current);
        clearHoverTimeoutRef.current = null;
      }
      if (currentHoveredRef.current) {
        currentHoveredRef.current = null;
        setHoveredNodeId(null);
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (clearHoverTimeoutRef.current) {
        clearTimeout(clearHoverTimeoutRef.current);
      }
    };
  }, [containerRef]);

  return hoveredNodeId;
}

export default useHoveredNode;
